import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Clock, CheckCircle, Star, ArrowRight, Sparkles } from 'lucide-react';
import { serviceService } from '../services/api';
import { LocalBusinessSchema, BreadcrumbSchema } from '../components/SchemaMarkup';

const Location = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  const area = location.pathname.replace('/car-service-', '');
  const areaName = area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await serviceService.getAll();
        if (response.success) {
          setServices(response.services.filter(s => s.is_active).slice(0, 4));
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const topAreas = [
    'Koramangala', 'Indiranagar', 'Whitefield', 'Electronic City',
    'HSR Layout', 'BTM Layout', 'Marathahalli', 'Bellandur',
    'Jayanagar', 'Sarjapur Road', 'JP Nagar', 'Hebbal',
    'Rajajinagar', 'Malleshwaram', 'Yelahanka'
  ];

  const benefits = [
    { icon: MapPin, title: `Free Pickup & Drop in ${areaName}`, desc: `Complimentary pickup and drop anywhere in ${areaName}. No extra charges.` },
    { icon: Clock, title: 'Same-Day Service', desc: 'Quick turnaround — most services completed within 5-6 hours.' },
    { icon: CheckCircle, title: '6 Months Warranty', desc: 'All replaced parts come with a 6-month warranty.' },
    { icon: Star, title: 'Expert Technicians', desc: 'Certified mechanics trained to service all car brands and models.' },
    { icon: Phone, title: 'Transparent Updates', desc: 'Regular communication via email — always know your car\'s status.' },
    { icon: CheckCircle, title: 'All Brands Serviced', desc: 'From hatchbacks to luxury cars — we service every make and model.' }
  ];

  const breadcrumbItems = [
    { name: 'Home', url: 'https://propitstop.in/' },
    { name: `Car Service in ${areaName}`, url: `https://propitstop.in/car-service-${area}` }
  ];

  return (
    <div data-testid="location-page" className="min-h-screen bg-slate-950 text-slate-200">
      <LocalBusinessSchema areaName={areaName} />
      <BreadcrumbSchema items={breadcrumbItems} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 pt-24 pb-20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(6,182,212,0.18),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_70%,rgba(59,130,246,0.12),transparent_55%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <MapPin className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-medium">{areaName}, Bangalore</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-3xl">
            Car service in <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{areaName}</span>, done right
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Premium car service and repair at your doorstep in {areaName}. Transparent, certified, and genuinely good.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => navigate('/booking')} data-testid="location-book-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl px-6 py-6 shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:shadow-[0_0_30px_rgba(6,182,212,0.45)] transition-[box-shadow,background-color] duration-300">
              Book Service Now
            </Button>
            <Button onClick={() => navigate('/services')} variant="outline"
              className="border-slate-700 bg-slate-900/40 text-slate-200 hover:bg-slate-900 hover:text-white hover:border-cyan-500/40 rounded-xl px-6 py-6">
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Why Propitstop</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              Built for {areaName}'s busy drivers
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i}
                  className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/40 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-cyan-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Services</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              Car services in {areaName}
            </h2>
            <p className="mt-3 text-slate-400">Professional care at your doorstep.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index}
                className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/40 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_22px_rgba(6,182,212,0.15)]">
                <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
                <p className="text-sm text-slate-400 line-clamp-3 mb-4">{service.description}</p>
                <p className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight mb-4">
                  ₹{service.base_price}
                </p>
                <Button onClick={() => navigate('/booking')}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">
                  Book Now
                </Button>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button className="bg-transparent border border-slate-700 text-slate-200 hover:bg-slate-900 hover:text-white hover:border-cyan-500/40 rounded-xl px-6 py-6">
                View All Services <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About area */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">{areaName} Car Service</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              Professional car service in {areaName}
            </h2>
          </div>
          <div className="space-y-4 text-slate-400 leading-relaxed">
            <p>
              Looking for reliable car service in {areaName}? Propitstop is your trusted partner for all car maintenance and repair needs.
              We bring professional automotive care right to your doorstep in {areaName}, Bangalore.
            </p>
            <p>
              Our {areaName} service center is equipped with state-of-the-art diagnostic tools and staffed by experienced technicians who
              specialize in all car brands — Maruti, Hyundai, Honda, Toyota, BMW, Mercedes and more.
            </p>
            <p>
              We understand the busy lifestyle of {areaName} residents. That's why we offer free pickup and drop service — book online and
              we'll collect your vehicle, service it at our facility, and return it. No extra charges.
            </p>
            <p>
              Services include periodic maintenance, engine diagnostics, AC repair, denting and painting, vehicle inspection, and more.
              We use only genuine or OEM-equivalent parts and provide a 6-month warranty on all replaced parts.
            </p>
          </div>

          <div className="mt-10 p-6 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-cyan-500/20">
            <h3 className="text-base font-bold text-white mb-4">Contact us for {areaName} service</h3>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Phone</p>
                <a href="tel:+919110416663" className="text-cyan-400 hover:text-cyan-300">+91 91104 16663</a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Email</p>
                <a href="mailto:support@propitstop.in" className="text-cyan-400 hover:text-cyan-300 break-all">support@propitstop.in</a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Hours</p>
                <p className="text-slate-300">Mon–Sat: 9AM–7PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other areas */}
      <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">More Areas</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-white tracking-tight">Other areas we serve</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {topAreas.filter(a => a !== areaName).map((a, index) => (
              <Link
                key={index}
                to={`/car-service-${a.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex items-center gap-2 p-4 rounded-xl bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 hover:border-cyan-500/40 transition-colors"
              >
                <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-200 group-hover:text-white">{a}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 border border-slate-800 px-8 py-14 md:px-16 md:py-16 text-center">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(6,182,212,0.22),transparent_55%)]" />
            </div>
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
                Ready to service your car in {areaName}?
              </h2>
              <p className="mt-3 text-slate-400">Book now and get your car serviced by certified experts.</p>
              <Button onClick={() => navigate('/booking')}
                className="mt-6 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl px-6 py-6 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-[box-shadow,background-color] duration-300">
                Book Service Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
